<template>
  <v-container fluid>
    <v-layout row wrap>
      <v-flex md12 xs12 d-flex>
        <v-card>
          <v-card-title class="pb-0">
            <v-layout align-center justify-space-between fill-height>
              <v-flex xs12>
                <h5 class="headline text-uppercase text-green">
                  {{ `app.mobile_app.header` | trans }}
                </h5>
              </v-flex>
            </v-layout>
          </v-card-title>
          <v-card-text>
            <v-divider></v-divider>
            <v-container fluid>
              <v-layout wrap>
                <v-flex xs12 v-if="hasEmail">
                  <span class="subheading">{{ `app.mobile_app.status` | trans }}:</span>
                  <span :class="{'text-green': appConfigured, 'text-orange': invitedToMobileApp || invitationAccepted}">{{
                      mobileAppStatus
                    }}</span>
                </v-flex>
                <v-flex xs12 v-if="appConfigured">
                  <span class="subheading">{{ `app.mobile_app.notifications_enabled` | trans }}:</span>
                  <span class="text-green" v-if="mobileAppAccess.settings.notificationsEnabled">{{
                      `app.enabled` | trans
                    }}</span>
                  <span class="text-orange" v-else>{{ `app.disabled` | trans }}</span>
                </v-flex>
                <v-flex xs12 v-if="settingsAvailable && hasPermission('prescription_mobile_app_send')">
                  <span class="subheading">{{ `app.mobile_app.prescriptions_enabled` | trans }}:</span>
                  <span class="text-green" v-if="mobileAppAccess.settings.prescriptionsEnabled">{{
                      `app.enabled` | trans
                    }}</span>
                  <span class="text-orange" v-else>{{ `app.disabled` | trans }}</span>
                </v-flex>
                <v-flex xs12 v-if="appConfigured">
                  <span class="subheading">{{ `app.mobile_app.version` | trans }}:</span>
                  <span>{{ mobileAppAccess.app_version }}</span>
                </v-flex>
                <v-flex xs12 v-if="hasEmail && !invitationAccepted && !appConfigured">
                  <v-layout row align-center justify-center>
                    <v-flex xs12 class="text-xs-center">
                      <v-btn color="primary" @click="sendInvitation()" :disabled="loading">
                        {{
                          (invitedToMobileApp ? 'app.mobile_app.resend_invitation' : 'app.mobile_app.send_invitation') | trans
                        }}
                      </v-btn>
                    </v-flex>
                  </v-layout>
                </v-flex>
                <v-flex xs12 v-if="!hasEmail">
                  <v-alert :value="true" type="warning">
                    <div v-html="getEmailMissing()"></div>
                  </v-alert>
                </v-flex>
              </v-layout>
            </v-container>
            <v-divider v-if="settingsAvailable && hasPermission('prescription_mobile_app_send')"/>
            <v-container v-if="settingsAvailable && hasPermission('prescription_mobile_app_send')" fluid>
              <v-layout row wrap>
                <v-flex xs8>
                  <h4>{{ `app.mobile_app.prescriptions.header` | trans }}</h4>
                </v-flex>
                <v-flex xs4>
                  <v-btn class="pull-right" color="primary" @click="updatePrescriptions()"
                         :loading="prescriptionsLoading">
                    {{ 'app.save' | trans }}
                  </v-btn>
                </v-flex>
              </v-layout>
            </v-container>
            <v-container v-if="settingsAvailable && hasPermission('prescription_mobile_app_send')" fluid grid-list-md>
              <v-layout align-center>
                <v-flex xs3 sm3 md3 d-flex class="prescriptions-checkbox-wrapper">
                  <v-checkbox :class="`mt-0 pt-0`" :color="`primary`" v-model="prescriptions.enabled"
                              :label="`app.mobile_app.prescriptions.enabled`|trans"/>
                </v-flex>
                <v-flex xs2 sm2 md2>
                  <permission-button
                      :permission="canSelectPrescription"
                      :tooltip="{top: true, bottom: false, attach: false}"
                      :no-permission-tool-tip="trans('app.mobile_app.prescriptions.limit', {limit: 5})"
                      color="primary"
                      class="ml-0 mr-0" @click="openSelectPrescriptionDialog">
                    {{ `app.mobile_app.prescriptions.select` | trans }}
                  </permission-button>
                </v-flex>
              </v-layout>
            </v-container>
            <mobile-app-prescriptions-table
                v-if="settingsAvailable && hasPermission('prescription_mobile_app_send')"
                ref="prescriptionsTable" :patient_id="patient_id" @prescriptionRemoved="prescriptionRemoved"/>
            <v-divider v-if="settingsAvailable"/>
            <nutrients-form ref="nutrientsForm" v-if="settingsAvailable" :title="`app.mobile_app.nutrients.header` | trans"
                            :max-nutrients="12" :route="`/v2/mobile-app/${patient_id}/displayed-nutrients`"
                            :permission="'patient_mobile_app_invite'"/>
          </v-card-text>
        </v-card>
      </v-flex>
    </v-layout>
    <print-prescription-dialog
        :url="'/v2/prescriptions'"
        ref="selectPrescriptionDialog"
        @prescription-selected="prescriptionSelected"
    />
  </v-container>
</template>
<script>
import {mapActions, mapGetters, mapMutations} from "vuex";
import {trans} from "../../../app";
import MobileAppService from "../../../services/MobileAppService";

export default {
  props: {
    patient_id: {
      type: Number,
    },
  },
  data() {
    return {
      mobileAppService: new MobileAppService(),
      loading: false,
      prescriptionsLoading: false,
      prescriptions: {
        enabled: false,
        selectedPrescriptions: [],
      },
    }
  },
  mounted() {
    this.fetchData();
  },
  methods: {
    ...mapMutations(['setExcludedPrescriptionIds']),
    ...mapActions(['fetchPatient', 'fetchMobileAppAccess']),
    getEmailMissing() {
      return trans('app.mobile_app.email_missing', {'patient_id': this.patient_id});
    },
    sendInvitation() {
      this.loading = true;
      this.mobileAppService.invitePatient(this.patient_id)
          .then(() => {
            this.fetchMobileAppAccess(this.patient_id)
                .then(() => {
                  this.$toast.success(trans('app.mobile_app.invitation_sent'));
                  this.loadMobileAppSettings().then(() => {
                    this.loading = false;
                  }).catch((error) => {
                    this.loading = false;
                    this.sentryCaptureException(error);
                  })
                }).catch((error) => {
                  this.loading = false;
                  this.$toast.error(trans('app.error_occurred'));
                  this.sentryCaptureException(error);
                });
          }).catch((error) => {
            this.loading = false;
            this.$toast.error(trans('app.error_occurred'));
            this.sentryCaptureException(error);
      });
    },
    updatePrescriptions() {
      this.prescriptionsLoading = true;
      this.mobileAppService.updatePrescriptions(
          this.patient_id,
          this.prescriptions.enabled,
          this.prescriptions.selectedPrescriptions
      ).then(() => {
        this.prescriptionsLoading = false;
        this.$toast.success(trans('app.saved'));
        this.fetchData();
        this.$refs.prescriptionsTable.fetchData();
      })
          .catch((error) => {
            this.prescriptionsLoading = false;
            this.$toast.error(trans('app.error_occurred'));
            this.sentryCaptureException(error);
          })
    },
    fetchData() {
      this.fetchPatient(this.patient_id).then(() => {
        this.fetchMobileAppAccess(this.patient_id).then(() => {
          this.loadMobileAppSettings().then(()=> {})
              .catch((error) => {
                this.sentryCaptureException(error);
              })
        })
      });
    },
    openSelectPrescriptionDialog() {
      this.$refs.selectPrescriptionDialog.show();
    },
    prescriptionSelected(prescription) {
      this.prescriptions.selectedPrescriptions.unshift(prescription.id);
      this.$refs.prescriptionsTable.addPrescription(prescription);
    },
    prescriptionRemoved(prescription) {
      this.prescriptions.selectedPrescriptions = this.prescriptions.selectedPrescriptions.filter((id) => {
        return id !== prescription.id;
      });
    },
    async loadMobileAppSettings() {
      if (this.settingsAvailable) {
        this.prescriptions.enabled = this.mobileAppAccess.settings.prescriptionsEnabled;
        this.prescriptions.selectedPrescriptions = this.mobileAppAccess.prescriptions.map((prescription) => {
          return prescription.id;
        });
        await this.$nextTick;
        await this.$refs.nutrientsForm.setCurrentNutrients(this.mobileAppAccess.settings.displayedNutrients);
      }
    }
  },
  computed: {
    ...mapGetters(['mobileAppAccess', 'patient', 'hasPermission']),
    hasEmail() {
      return !!this.patient.email;
    },
    invitedToMobileApp() {
      return this.mobileAppAccess.status === 'invited';
    },
    invitationAccepted() {
      return this.mobileAppAccess.status === 'accepted';
    },
    appConfigured() {
      return this.mobileAppAccess.status === 'app_configured';
    },
    settingsAvailable() {
      return this.invitedToMobileApp || this.invitationAccepted || this.appConfigured;
    },
    mobileAppStatus() {
      return trans('app.mobile_app.statuses.' + this.mobileAppAccess.status);
    },
    canSelectPrescription() {
      return this.prescriptions.selectedPrescriptions.length < 5;
    }
  },
  watch: {
    'prescriptions.selectedPrescriptions': {
      handler(val) {
        this.setExcludedPrescriptionIds(val);
      }
    }
  }
}
</script>
<style scoped>
.prescriptions-checkbox-wrapper ::v-deep .v-input__slot {
  margin-bottom: 0;
}

.prescriptions-checkbox-wrapper ::v-deep .v-messages {
  display: none;
}
</style>
